/**
 * MissionStats Component
 *
 * Displays real-time mission statistics in a panel
 */

import { Gauge, Clock, Mountain, Activity, Zap, MapPin } from 'lucide-react';
import { useMissionCalculator } from '../hooks/useMissionCalculator';
import { formatDistance, formatTime, formatBattery } from '../utils/mission-calculator';
import { cn } from '@/lib/utils';

interface MissionStatsProps {
  className?: string;
}

export function MissionStats({ className }: MissionStatsProps) {
  const { statistics } = useMissionCalculator();

  const batteryInfo = formatBattery(statistics.estimatedBatteryUsage);

  // Determine if mission is safe based on battery
  const needsAttention = statistics.estimatedBatteryUsage >= 70 && statistics.estimatedBatteryUsage < 90;
  const isDangerous = statistics.estimatedBatteryUsage >= 90;

  return (
    <div className={cn('bg-slate-900 border-t border-slate-700 p-4 rounded-lg', className)}>
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-white flex items-center gap-2">
          <Activity className="w-4 h-4" />
          Mission Statistics
        </h3>
        {statistics.waypointCount > 0 && (
          <span className="text-xs text-slate-400">
            {statistics.waypointCount} waypoints
          </span>
        )}
      </div>

      {statistics.waypointCount === 0 ? (
        <div className="text-center py-8 text-slate-500 text-sm">
          <MapPin className="w-8 h-8 mx-auto mb-2 opacity-50" />
          <p>No waypoints yet</p>
          <p className="text-xs mt-1">Click on the map to add waypoints</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {/* Total Distance */}
          <StatCard
            icon={<Gauge className="w-4 h-4" />}
            label="Distance"
            value={formatDistance(statistics.totalDistance)}
            color="text-white"
          />

          {/* Flight Time */}
          <StatCard
            icon={<Clock className="w-4 h-4" />}
            label="Est. Time"
            value={formatTime(statistics.estimatedFlightTime)}
            color="text-white"
          />

          {/* Battery Usage */}
          <StatCard
            icon={<Zap className="w-4 h-4" />}
            label="Battery"
            value={batteryInfo.text}
            color={
              batteryInfo.color === 'green'
                ? 'text-white'
                : batteryInfo.color === 'yellow'
                  ? 'text-white'
                  : 'text-white'
            }
            warning={needsAttention}
            danger={isDangerous}
          />

          {/* Max Altitude */}
          <StatCard
            icon={<Mountain className="w-4 h-4" />}
            label="Max Alt"
            value={`${statistics.maxAltitude.toFixed(0)}m`}
            color="text-white"
          />

          {/* Min Altitude */}
          <StatCard
            icon={<Mountain className="w-4 h-4 rotate-180" />}
            label="Min Alt"
            value={`${statistics.minAltitude.toFixed(0)}m`}
            color="text-white"
          />

          {/* Average Speed */}
          <StatCard
            icon={<Activity className="w-4 h-4" />}
            label="Avg Speed"
            value={`${statistics.averageSpeed.toFixed(1)} m/s`}
            color="text-white"
          />
        </div>
      )}

      {/* Warnings */}
      {needsAttention && (
        <div className="mt-4 p-3 bg-yellow-900/30 border border-yellow-700/50 rounded-md">
          <p className="text-xs text-white flex items-center gap-2">
            <Zap className="w-4 h-4" />
            High battery usage. Consider shortening mission or adding battery reserve.
          </p>
        </div>
      )}

      {isDangerous && (
        <div className="mt-4 p-3 bg-red-900/10 border border-red-700/50 rounded-md">
          <p className="text-xs text-red-600 flex items-center gap-2">
            <Zap className="w-4 h-4" />
            Critical: Mission exceeds battery capacity! Reduce distance or waypoints.
          </p>
        </div>
      )}
    </div>
  );
}

/**
 * Individual stat card component
 */
interface StatCardProps {
  icon: React.ReactNode;
  label: string;
  value: string;
  color?: string;
  warning?: boolean;
  danger?: boolean;
}

function StatCard({ icon, label, value, color = 'text-slate-300', warning, danger }: StatCardProps) {
  return (
    <div
      className={cn(
        'bg-slate-800 rounded-lg p-3 border transition-all',
        danger ? 'border-red-700 bg-red-900/20' : warning ? 'border-yellow-700 bg-yellow-900/20' : 'border-slate-700'
      )}
    >
      <div className="flex items-center gap-2 mb-1">
        <span className={cn(color, 'opacity-80')}>{icon}</span>
        <span className="text-xs text-slate-400">{label}</span>
      </div>
      <p className={cn('text-lg font-semibold', color)}>{value}</p>
    </div>
  );
}
